<template>
  <div
    class="tag-input"
    :class="{
      '--focused': inputFocused,
      '--disabled': disabled,
      '--empty': !tags.length,
    }"
    tabindex="0"
    @click.stop="activeIndexToDelete = null"
    ref="tagInputContainer"
  >
    <div
      v-for="(tag, index) in tags"
      class="tag-input__tag"
      :class="{'--deleting': index === activeIndexToDelete, '--invalid': !isValid(tag) } "
      @click="activeIndexToDelete = index"
      :key="tag"
      tabindex="0"
    >
      <span class="flex-1">{{ tag }}</span>
      <span @click="removeTag(index)">
        <aha-icon
          name="close"
          width="10"
          height="10"
          viewBox="0 0 24 24"
        />
      </span>
    </div>
    <!-- 186 = semi-colon -->
    <!-- 188 = comma -->
    <input
      type="text"
      :placeholder="placeholder"
      class="tag-input__input"
      :disabled="disabled"
      @focus="handleFocusInput"
      @blur="handleInputBlur"
      @keydown="onKeyDownHandler"
      @keydown.delete="removeLastTag"
      @keydown.tab="addTag"
      @keydown.enter="addTag"
      :spellcheck="spellcheck"
      :autocomplete="autocomplete"
    >
  </div>
</template>
<script>
import Validate from '../../utils/validate-email'

import AhaIcon from './Icon.vue'

// inspiration: https://learnvue.co/2020/01/build-a-custom-vuejs-tag-input-in-under-10-minutes/
export default {
  components: {
    AhaIcon,
  },
  props: {
    value: {
      type: Array,
      default: () => [],
    },
    validator: {
      type: [String, Function],
      default: null,
    },
    placeholder: {
      type: String,
      default: 'Enter a Tag',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    formatter: {
      type: Function,
      default: null,
    },
    allowDuplicate: {
      type: Boolean,
      default: true,
    },
    spellcheck: {
      type: Boolean,
      default: false,
    },
    autocomplete: {
      type: String,
      default: 'off',
    },
    tagType: {
      type: String,
      default: '',
    },
    supportSpaces: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      tags: [],
      activeIndexToDelete: null,
      inputFocused: true,
    }
  },
  watch: {
    value: {
      immediate: true,
      handler(newValue) {
        this.tags = newValue
      },
    },
  },
  mounted() {
    this.regex = new RegExp(this.validator)
  },
  methods: {
    addTag(event) {
      // Don't know why the commit 490b11c4c93457f01eb56483a5eb48a1708f129f use the trim().replace(',', '')
      // But it will cause the filtering out the first comma.
      // So I work around with this condition
      const val = this.supportSpaces ? event.target.value.trim() : event.target.value.trim().replace(',', '')
      if (val === ',') return
      if (val.length > 0 && val !== ',') {
        const entries = this.parseEntries(val)
        if (entries.length) {
          entries.forEach((entry) => {
            let entryToAdd = entry
            if (this.formatter && typeof this.formatter === 'function') {
              entryToAdd = this.formatter(entryToAdd)
            }
            if (!this.allowDuplicate) {
              if (this.tags.indexOf(entryToAdd) > -1) return
            }
            if (entryToAdd.length) {
              if (!this.tagType) {
                this.tags.push(entryToAdd)
              } else if (this.tagType === 'email' && Validate.validateEmail(entryToAdd)) {
                this.tags.push(entryToAdd)
              }
            }
          })
          this.$emit('input', this.tags)
          event.target.value = ''
          this.$emit('tagExists', false)
        }
      }
    },

    handleFocusInput() {
      this.inputFocused = true
      const container = this.$refs.tagInputContainer
      if (container) {
        container.scrollTop = container.scrollHeight
      }
    },

    // eslint-disable-next-line max-len
    // input: 1@a.com,2@a.com ,3@a.com, 4@a.com; 5@a.com;6@a.com ;7@x.com 8@x.com     9@x.com ,      10@x.com ee,ae
    // eslint-disable-next-line max-len
    // output: [1@a.com, 2@a.com, 3@a.com, 4@a.com, 5@a.com, 6@a.com, 7@x.com, 8@x.com, 9@x.com, 10@x.com, ee, ae]
    parseEntries(string) {
      // look for: , and ; and space character
      let regex = /(\s*,\s*|\s*;\s*|\s+)/ig
      if (this.supportSpaces) {
        regex = /(\s*,\s*|\s*;\s*)/ig
      }
      const entries = string.replace(regex, ',')
      // split by ,
      return entries.split(',')
    },

    removeTag(index) {
      this.tags.splice(index, 1)
      this.$emit('input', this.tags)
    },
    clearAllTags() {
      this.tags = []
    },
    onKeyDownHandler(event) {
      // 188: ,
      // 59: ;
      // 32: space
      let supportKeyList = [188, 59, 32]
      if (this.supportSpaces) {
        supportKeyList = [188, 59]
      }
      if (supportKeyList.includes(event.keyCode)) {
        event.preventDefault()
        this.addTag(event)
      }
    },
    removeLastTag(event) {
      if (event.target.value.trim()) return
      if (!Number.isInteger(this.activeIndexToDelete)) {
        this.activeIndexToDelete = this.tags.length - 1
        return
      }
      this.removeTag(this.activeIndexToDelete)
      this.activeIndexToDelete = null
    },
    isValid(tag) {
      if (!this.validator) return true
      if (typeof this.validator === 'string') { // regex
        return this.regex.test(tag)
      }
      if (typeof this.validator === 'function') { // validator function
        return this.validator(tag)
      }
      return false
    },
    handleInputBlur(event) {
      this.inputFocused = false
      this.addTag(event)
    },
  },
}

</script>
<style lang="scss" scoped>
.tag-input {
  min-height: 40px;
  @apply flex items-center flex-wrap w-full border border-secondary-20 rounded;
  @apply hover:bg-primary-20 hover:border-primary-90;

  &.--focused {
    @apply bg-primary-20 border-primary-90 outline-none;
  }

  &.--disabled {
    @apply bg-primary-20 border-secondary-20 outline-none;
  }

  &__tag {
    @apply text-base-90 flex bg-base-0 justify-center items-center;
    @apply border border-base-60 rounded m-1 text-md14-h18 py-1 px-2;

    &:focus {
      background: #348EF6 !important;
      color: #fff !important;
      border: 1px solid transparent !important;
    }

    &.--invalid {
      @apply border border-red-50 text-red-50;
    }

    &.--deleting {
      @apply bg-red-50 text-base-0 border border-transparent;
    }
  }

  &__tag > span {
    @apply cursor-pointer ml-1;
  }

  &__input {
    width: 5px;
    height: 28px;
    @apply text-md14-h18 py-1 px-2 flex-1 border-none outline-none;
    @apply appearance-none placeholder-base-70 bg-transparent;

    &:focus {
      width: 90px;
    }
  }
}

</style>
